import { getSecurityAmender } from "src/modules/helpers/authentication-helper";
import { z } from "zod";
import { fetchZ, postBody } from "./service-helper";
import { fetchOk } from "src/datacraft-util";

const securityAmender = getSecurityAmender();

const monthlyValueSchema = z.object({
  plannedAbsence: z.number().nullish(),
});

const employeeSubmissionSchema = z.object({
  mcnzNumber: z.string().optional().nullable(),
  forenames: z.string().nullish(),
  surname: z.string().nullish(),
  role: z.string().nullish(),
  isLocum: z.boolean().optional().nullable(),
  hoursPerWeek: z.number().nullish(),
  startDate: z.string().optional().nullable(), //could use z.coerce.date, but using strings works better with Tanstack Forms.
  endDate: z.string().optional().nullable(),
  monthlyValues: z.record(z.string(), monthlyValueSchema).optional().nullable(),
  additionalFields: z.record(
    z.string(),
    z.union([z.string(), z.array(z.string())])
  ).optional().nullable(),
});

const employeeSchema = employeeSubmissionSchema.transform((employee) => ({
  ...employee,
  uniqueId: crypto.randomUUID(),
  isSaved: true,
}));

const dropdownOptionSchema = z.object({
  code: z.string(),
  displayName: z.string()
})

export const configurableFieldSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  options: z.array(dropdownOptionSchema).optional().nullable(),
})

const workforceRequestSchema = z.object({
  requestName: z.string(),
  hasSubmitted: z.boolean(),
  isOverdue: z.boolean()
});

export const workforceSubmissionSchema =  z.object({
  employees: z.array(employeeSubmissionSchema),
  enrolmentStatus: z.string().optional().nullable()
});

export const workforceFormDataSchema = z.object({
    htmlContent: z.string().optional().nullable(),
    facilityName:z.string(),
    employees: z.array(employeeSchema),
    enrolmentStatus: z.string().optional().nullable(),
    helpLink: z.string().optional().nullable(),
    roles: z.array(z.string()),
    configuredColumns: z.array(configurableFieldSchema),
    configuredFacilityFields: z.array(configurableFieldSchema),
    activeWorkforceRequest: workforceRequestSchema.optional().nullable()
}).transform((val) => ({ 
  ...val, 
  employeeOrder: val.employees.map(e => e.uniqueId),
  keyedEmployees: Object.fromEntries(val.employees.map(e => [e.uniqueId, e]))
}));

const workforceFacilitiesSchema = z.array(z.object({
  facilityReference:z.string(),
  facilityName:z.string(), 
  activeRequestName:z.string(),
  isRequested:z.boolean(),
  isOverdue:z.boolean()
}));

export type WorkforceFormDataType = z.infer<typeof workforceFormDataSchema>
export type WorkforceSubmissionType = z.infer<typeof workforceSubmissionSchema>
export type WorkforceEmployeeType = z.infer<typeof employeeSchema>
export type WorkforceSubmissionEmployeeType = z.infer<typeof employeeSubmissionSchema>
export type WorkforceConfigurableFieldType = z.infer<typeof configurableFieldSchema>
export type WorkforceDropdownOptionType = z.infer<typeof dropdownOptionSchema>
export type WorkforceRequestType = z.infer<typeof workforceRequestSchema>

export const workforceService = {
    listFacilities:async () => await fetchZ(workforceFacilitiesSchema, `/api/workforce/list-facilities`, await securityAmender.AppendSecurityToRequest({})),
    getCurrentForm:async (facilityReference:string) => await fetchZ(workforceFormDataSchema, `/api/workforce/get-form-state/${facilityReference}`, await securityAmender.AppendSecurityToRequest({})),
    updateForm:async (facilityReference:string, submitRequest: boolean, data: WorkforceSubmissionType) => await fetchOk(`/api/workforce/set-form-state/${facilityReference}/${submitRequest}`, await postBody(data)),
}